import { graphql } from "@acto/gql";
import { DateRangeInput } from "@acto/gql/graphql";
import { useMemo } from "react";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";

const EmployeeClientsQuery = graphql(`
  query GetEmployeeClients(
    $employeeId: ID!
    $first: Int!
    $after: String
    $dateRange: DateRangeInput!
    $comparisonMode: KpiComparisonMode!
  ) {
    team {
      employeeClients(employeeId: $employeeId, first: $first, after: $after, dateRange: $dateRange) {
        edges {
          node {
            id
            name
            externalReference
            revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
              totalValue
              percentageChange
            }
            profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
              totalValue
              percentageChange
            }
            margin(dateRange: $dateRange, comparisonMode: $comparisonMode) {
              totalValue
              percentageChange
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export interface EmployeeClient {
  id: string;
  name: string;
  externalReference: string | null;
  revenue: number | null;
  revenueChange: number | null;
  margin: number | null;
  marginChange: number | null;
  profit: number | null;
  profitChange: number | null;
}

interface UseEmployeeClientsOptions {
  employeeId: string | null;
  dateRange: DateRangeInput;
}

interface UseEmployeeClientsResult {
  clients: EmployeeClient[];
  isFetching: boolean;
  isPending: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | undefined;
}

const PAGE_SIZE = 5;

const EMPTY_CONNECTION = {
  edges: [],
  pageInfo: { hasNextPage: false, endCursor: null },
};

export function useEmployeeClients({ employeeId, dateRange }: UseEmployeeClientsOptions): UseEmployeeClientsResult {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());
  const variables = useMemo(
    () => ({ employeeId: employeeId ?? "", dateRange, comparisonMode }),
    [employeeId, dateRange, comparisonMode],
  );

  const { data, isFetching, isPending, hasNextPage, fetchNextPage, error } = usePagination({
    query: EmployeeClientsQuery,
    limit: PAGE_SIZE,
    enabled: !!employeeId,
    variables,
    connection: (queryData) => queryData.team?.employeeClients ?? EMPTY_CONNECTION,
  });

  const clients = useMemo<EmployeeClient[]>(
    () =>
      (data ?? []).map((node) => {
        return {
          id: node.id,
          name: node.name,
          externalReference: node.externalReference ?? null,
          revenue: node.revenue?.totalValue ?? null,
          revenueChange: node.revenue?.percentageChange ?? null,
          margin: node.margin?.totalValue ?? null,
          marginChange: node.margin?.percentageChange ?? null,
          profit: node.profit?.totalValue ?? null,
          profitChange: node.profit?.percentageChange ?? null,
        };
      }),
    [data],
  );

  return {
    clients,
    isFetching,
    isPending,
    hasNextPage,
    fetchNextPage,
    error,
  };
}
